import React, { useState, useRef, useEffect, useContext } from "react";
import { isPlatform } from "@ionic/react";
// import soundOnIcon from "../../assets/soundOn.svg";
// import soundOffIcon from "../../assets/soundOff.svg";
import { welcomeAudiosWithLipSync } from "../../mockup/welcomeAudios";
// import testAudio from "../../assets/test.mp3";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import VolumeDownIcon from "@mui/icons-material/VolumeDown";
import VolumeMuteIcon from "@mui/icons-material/VolumeMute";
import { Box, Button, Collapse, useMediaQuery, useTheme } from "@mui/material";
// import { PureLightThemeCaaS } from "src/theme/schemes/PureLightTheme";
import { audioService, AudioPriority } from "src/services/audioService";
import { applyVegaFromAudioUrlIfEnabled, isVegaLipSyncEnabled, prefetchVegaFromAudioUrl, tryStartVegaHtmlAudioTap } from "src/lipsync/vegaLipSync";
import { trackAudioMuted, trackAudioUnmuted } from "src/utils/analyticsCapFirebase";
import { WidgetContext } from "src/contexts/WidgetContext";
import AudioSlider from "./AudioSlider";
import { CharacterEmbedRef } from "src/components/CharacterViewer/CharacterEmbed";
import { CharacterEmbed2Ref } from "src/components/CharacterViewer/CharacterEmbed2";
import { PureLightThemeCaaS } from "src/theme/schemes/PureLightTheme";

// Welcome audio runs only once per session (AudioSound can mount twice: homepage overlay vs floating bubble)
let welcomeAudioHandledThisSession = false;

interface AudioSoundProps {
  shouldPlayAudio?: boolean;
  authId?: string;
  onSliderVisibilityChange?: (isVisible: boolean) => void;
  // characterRef?: React.RefObject<CharacterEmbedRef>;
  characterRef?: React.RefObject<CharacterEmbedRef | CharacterEmbed2Ref | null>;
  isFloating?: boolean;
  isCharacterReady?: boolean;
}

const AudioSound: React.FC<AudioSoundProps> = ({ shouldPlayAudio = false, authId, onSliderVisibilityChange, characterRef, isFloating, isCharacterReady = true }) => {
  // get the selected team info from the context
  const { selectedTeamInfo, aiSettings, setAiSettings, forceSavePreferences } = useContext(WidgetContext);
  const { platformId, leagueId, teamName } = selectedTeamInfo || {};
  // This local state will now be a mirror of the service's state
  const [isMuted, setIsMuted] = useState(audioService.getState().isMuted);
  const [volume, setVolume] = useState(() => audioService.getVolume() * 100);
  const [isSliderVisible, setIsSliderVisible] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const selectedAudioIndexRef = useRef<number>(-1);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Subscribe to the audio service on mount to listen for changes
  useEffect(() => {
    // When the service broadcasts a change, we update our local state
    const unsubscribeMute = audioService.subscribe(setIsMuted);
    const unsubscribeVolume = audioService.subscribeToVolume((newVolume) => {
      setVolume(newVolume * 100);
    });

    // Cleanup subscription when the component unmounts
    return () => {
      unsubscribeMute();
      unsubscribeVolume();
    };
  }, []);

  // Keep audioService mute state in sync with persisted user preference.
  useEffect(() => {
    const preferredMute = aiSettings?.mute;
    if (typeof preferredMute !== "boolean") return;
    audioService.setMuted(preferredMute);
  }, [aiSettings?.mute]);

  // Notify parent when slider visibility changes
  useEffect(() => {
    if (onSliderVisibilityChange) {
      onSliderVisibilityChange(isSliderVisible);
    }
  }, [isSliderVisible, onSliderVisibilityChange]);

  // useEffect to handle clicks outside on mobile to hide the slider
  useEffect(() => {
    if (!isMobile || !isSliderVisible) return;

    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsSliderVisible(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [isMobile, isSliderVisible]);

  // Initialize the welcome audio element and respect the mute state.
  // Run play/setup only once per session so switching bubble ↔ homepage doesn't replay welcome audio.
  useEffect(() => {
    if (welcomeAudioHandledThisSession) {
      return () => {
        if (characterRef?.current) {
          characterRef.current.setIsTalking(false);
        }
      };
    }

    const canPlayWelcomeAudio = !aiSettings?.quan || isCharacterReady;
    if (!canPlayWelcomeAudio) {
      return;
    }

    welcomeAudioHandledThisSession = true;

    // Select a random audio with its lip sync data
    const randomIndex = Math.floor(Math.random() * welcomeAudiosWithLipSync.length);
    selectedAudioIndexRef.current = randomIndex;
    const selectedWelcome = welcomeAudiosWithLipSync[randomIndex];

    // Create audio element properly for iOS/Android mixing
    const audio = document.createElement("audio");
    audio.setAttribute("playsinline", "");
    audio.setAttribute("preload", "auto");
    // iOS Capacitor: crossOrigin on local capacitor:// assets taints MediaElementSource (Vega tap fallback).
    if (!(isPlatform("hybrid") && isPlatform("ios"))) {
      audio.crossOrigin = "anonymous";
    }
    audio.src = selectedWelcome.audio;
    prefetchVegaFromAudioUrl(selectedWelcome.audio);

    audioRef.current = audio;

    const isHybrid = isPlatform("hybrid");
    let playAudioAfterUserInteraction: () => void;

    const playWelcomeAudioWithLipSync = async () => {
      if (!audioRef.current) return;

      // Set up character for animation (Idle/Talking) regardless of lip sync availability
      if (characterRef?.current) {
        // Set up the audio element on the character (handles ended event for Idle transition)
        characterRef.current.setAudioElement(audioRef.current);

        // Vega on: analyze the welcome MP3 on-device. Vega off: keep pre-baked Azure JSON.
        if (isVegaLipSyncEnabled()) {
          const usedVega = await applyVegaFromAudioUrlIfEnabled(selectedWelcome.audio, characterRef.current, "welcome");
          const tapped = !usedVega && tryStartVegaHtmlAudioTap(audioRef.current, characterRef.current, "welcome", () => {
            characterRef.current?.setIsTalking(true);
          });
          if (!usedVega && !tapped) {
            console.log("[Welcome Audio] Vega analysis failed; Azure JSON is not applied while Vega is on");
          }
          if (usedVega || !tapped) {
            characterRef.current.setIsTalking(true);
          }
        } else if (selectedWelcome.lipSync && selectedWelcome.lipSync.mouthCues) {
          console.log(`[LipSync] WELCOME using pre-baked Azure JSON (${selectedWelcome.lipSync.mouthCues.length} cues)`);
          console.log(`[Welcome Audio] Using pre-generated lip sync: ${selectedWelcome.lipSync.mouthCues.length} cues`);
          characterRef.current.setLipSyncData(selectedWelcome.lipSync);
          characterRef.current.setIsTalking(true);
        } else {
          console.log("[Welcome Audio] No lip sync data available, character will animate without mouth movements");
          characterRef.current.setIsTalking(true);
        }
      }

      // Use the audio service to play with low priority
      const played = await audioService.playAudio(audioRef.current, AudioPriority.LOW);

      // If audio wasn't played (due to priority), stop the character animation
      if (!played && characterRef?.current) {
        characterRef.current.setIsTalking(false);
      }
    };

    if (isHybrid) {
      // On Capacitor (iOS/Android), play audio automatically.
      playWelcomeAudioWithLipSync();
    } else {
      // On Web, require user interaction to play audio.
      playAudioAfterUserInteraction = () => {
        playWelcomeAudioWithLipSync();
        document.removeEventListener("click", playAudioAfterUserInteraction, true);
      };
      document.addEventListener("click", playAudioAfterUserInteraction, true);
    }

    // Cleanup function
    return () => {
      // Remove the event listener only if it was added for the web platform
      if (!isHybrid && playAudioAfterUserInteraction) {
        document.removeEventListener("click", playAudioAfterUserInteraction, true);
      }
      // Stop talking animation on cleanup
      if (characterRef?.current) {
        characterRef.current.setIsTalking(false);
      }
      // No need to manually pause here, the service now handles the audio lifecycle.
    };
  }, [characterRef, aiSettings?.quan, isCharacterReady]);

  // This effect for `shouldPlayAudio` remains the same
  useEffect(() => {
    if (shouldPlayAudio && audioRef.current) {
      // Stop and restart the current audio
      audioRef.current.pause();
      audioRef.current.currentTime = 0;

      // Play the new audio
      audioRef.current.play().catch((err) => console.log("Audio play error:", err));
    }

    // Cleanup function to stop audio when shouldPlayAudio becomes false
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
    };
  }, [shouldPlayAudio]);

  const handleVolumeChange = (event: Event, newValue: number | number[]) => {
    const newVolume = (Array.isArray(newValue) ? newValue[0] : newValue) / 100;

    // If muted and user moves slider, unmute first — keep prefs in sync (same as unmutes from icon)
    if (isMuted) {
      audioService.setMuted(false);
      const nextAiSettings = {
        ...(aiSettings || {}),
        mute: false,
        audio: true,
      } as Record<string, boolean>;
      setAiSettings(nextAiSettings);
      void forceSavePreferences({ aiSettings: nextAiSettings });
      if (authId && platformId && leagueId && teamName) {
        void trackAudioUnmuted(authId, platformId, leagueId, teamName);
      }
    }

    audioService.setVolume(newVolume);

    // Reset the auto-close timer when user interacts (only for desktop)
    if (!isMobile) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        setIsSliderVisible(false);
      }, 2000);
    }
  };

  // function to handle the toggle mute (and track the event)
  const handleToggleMute = () => {
    const nextMuted = !audioService.getState().isMuted;
    // When unmuting, restore volume to 100% if it was at 0 (e.g. user had dragged slider to zero)
    if (!nextMuted && audioService.getVolume() === 0) {
      audioService.setVolume(1);
    }
    audioService.setMuted(nextMuted);

    // Persist preference so it survives app restart.
    // Mute: silence playback + disable backend audio generation; unmute: reverse both flags.
    const nextAiSettings = {
      ...(aiSettings || {}),
      mute: nextMuted,
      audio: nextMuted ? false : true,
    } as Record<string, boolean>;
    setAiSettings(nextAiSettings);
    void forceSavePreferences({ aiSettings: nextAiSettings });

    // Track the event (mute or unmute).
    if (authId && platformId && leagueId && teamName) {
      if (nextMuted) {
        void trackAudioMuted(authId, platformId, leagueId, teamName);
      } else {
        void trackAudioUnmuted(authId, platformId, leagueId, teamName);
      }
    }
  };

  const handleIconPointerDown = (event: React.PointerEvent) => {
    // event.preventDefault();

    // if (isMobile) {
    //   // On mobile, the first tap that makes the slider visible should not toggle mute.
    //   // If the slider is already visible, then the icon works as a mute toggle.
    //     if (isSliderVisible) {
    //       handleToggleMute();
    //     } else {
    //       setIsSliderVisible(true);
    //     }
    //   } else {
    //   // On desktop, a click always toggles mute.
    //   handleToggleMute();
    // }

    event.preventDefault();
    handleToggleMute();
  };

  useEffect(() => {
    if (isSliderVisible && !isMobile) {
      // Clear any previous timer
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Set a new timer for 3 seconds
      timeoutRef.current = setTimeout(() => {
        setIsSliderVisible(false);
      }, 2000);
    }

    // Cleanup when the slider is hidden or the component is unmounted
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isSliderVisible, isMobile]);

  return (
    <Box
      ref={containerRef}
      onMouseEnter={!isMobile ? () => setIsSliderVisible(true) : undefined}
      onMouseLeave={!isMobile ? () => setIsSliderVisible(false) : undefined}
      sx={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        position: "relative",
        backgroundColor: "black",
        border: `1px solid ${PureLightThemeCaaS.colors.alpha.gray[100]}`,
        borderRadius: 20,
        overflow: "hidden",
        transition: "width 0.45s ease-in-out",
        height: isFloating ? 25 : 32,
      }}

    >
      {/* This button now tells the service to broadcast a change */}
      <Button
        sx={{
          width: isFloating ? "25px" : "30px",
          height: isFloating ? "25px" : "30px",
          minWidth: 0,
          cursor: "pointer",
          p: 0,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          bgcolor: "transparent",
          flexShrink: 0,

        }}
        onPointerDown={handleIconPointerDown}
      >
        {isMuted ? (
          <VolumeOffIcon sx={{ fontSize: isFloating ? "25px" : "26px", color: "white", transform: "translateX(0px)" }} />
        ) : volume === 0 ? (
          <VolumeOffIcon sx={{ fontSize: isFloating ? "25px" : "26px", color: "white", transform: "translateX(0px)" }} />
        ) : volume === 1 ? (
          <VolumeMuteIcon sx={{ fontSize: isFloating ? "25px" : "26px", color: "white" }} />
        ) : volume <= 50 ? (
          <VolumeDownIcon sx={{ fontSize: isFloating ? "25px" : "26px", color: "white", transform: "translateX(-2.5px)" }} />
        ) : (
          <VolumeUpIcon sx={{ fontSize: isFloating ? "25px" : "26px", color: "white", transform: "translateX(0px)" }} />
        )}
      </Button>
      {/* hide the slider on mobile (web and capacitor)*/}
      {!isMobile && (
        <Collapse in={isSliderVisible} orientation="horizontal" timeout={{ enter: 350, exit: 350 }} collapsedSize={0}>
          <Box
            onMouseMove={() => {
              if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
              }
              timeoutRef.current = setTimeout(() => {
                setIsSliderVisible(false);
              }, 2000);
            }}
            sx={{ pl: 1.5, pr: 1.5, display: "flex", alignItems: "center", width: "max-content" }}
          >
            <AudioSlider value={isMuted ? 0 : volume} onChange={handleVolumeChange} />
          </Box>
        </Collapse>
      )}
    </Box>
  );
};

export default AudioSound;
